import { v4 as uuidv4 } from 'uuid';
import {
  Criterion,
  CriterionListItem,
  FieldChoice,
  ListValueType,
  SelectionBehavior,
} from 'queries';
import { ICriterionHandler } from './ICriterionHandler';
import { ParticipantField, ParticipantFieldType } from '@app/api';

type Range = { min: null | number; max: null | number };

export class RangeCriterionHandler extends ICriterionHandler {
  handleCriterion(criterion: Criterion) {
    return criterion.listValueType === ListValueType.AGE_RANGE;
  }

  criterionHandleOthers() {
    return false;
  }

  private removeFromAllList(criterion: Criterion, range: Range) {
    const denyList = this.removeRangeInList(
      criterion.denyList as Range[],
      range,
    );

    const allowList = criterion.allowList
      ? this.removeRangeInList(criterion.allowList as Range[], range)
      : undefined;

    const automaticlyAcceptedList = this.removeRangeInList(
      (criterion.automaticlyAcceptedList || []) as Range[],
      range,
    );

    return {
      ...criterion,
      allowList,
      denyList,
      automaticlyAcceptedList,
    };
  }

  declineChoice(criterion: Criterion, { value }: FieldChoice): Criterion {
    if (!value || typeof value !== 'object') {
      return criterion;
    }
    const newCriterion = this.removeFromAllList(criterion, value);
    newCriterion.denyList = this.addRangeInList(newCriterion.denyList, value);
    return newCriterion;
  }

  acceptChoice(criterion: Criterion, { value }: FieldChoice): Criterion {
    if (!value || typeof value !== 'object') {
      return criterion;
    }
    const newCriterion = this.removeFromAllList(criterion, value);
    if (newCriterion.allowList) {
      newCriterion.allowList = this.addRangeInList(
        newCriterion.allowList,
        value,
      );
    }
    return newCriterion;
  }

  tolerateChoice(criterion: Criterion, choice: FieldChoice): Criterion {
    return this.declineChoice(criterion, choice);
  }

  removeChoice(criterion: Criterion, choice: FieldChoice): Criterion {
    return this.declineChoice(criterion, choice);
  }

  automaticallyAcceptChoice(
    criterion: Criterion,
    choice: FieldChoice,
  ): Criterion {
    const value = choice.value;
    if (!value || typeof value !== 'object') {
      return criterion;
    }
    const newCriterion = this.acceptChoice(criterion, choice);
    newCriterion.automaticlyAcceptedList = this.addRangeInList(
      (newCriterion.automaticlyAcceptedList || []) as Range[],
      value,
    );
    return newCriterion;
  }

  compareValues(item: CriterionListItem, choice: FieldChoice) {
    const value = choice.value;
    if (!item || typeof item !== 'object') {
      return false;
    }
    if (!value || typeof value !== 'object') {
      return false;
    }

    if ('location' in item) {
      return false;
    }

    const min = item.min;
    const max = item.max;

    if (!min && !max) {
      return false;
    }

    if (min) {
      if (!value.min) {
        return false;
      }

      if (value.min < min) {
        return false;
      }

      if (value.max && value.max < min) {
        return false;
      }
    }
    if (max) {
      if (!value.max) {
        return false;
      }

      if (value.min && value.min > max) {
        return false;
      }

      if (value.max && value.max > max) {
        return false;
      }
    }
    return true;
  }

  addRangeInList(list: Range[], value: Range): Range[] {
    const newList = [...list];
    newList.push(value);
    return this.regroupRanges(newList);
  }

  removeRangeInList(list: Range[], value: Range): Range[] {
    const min = value.min;
    const max = value.max;

    const newList = [...list];

    for (const index in newList) {
      const range = newList[index];
      if (typeof range !== 'object') {
        continue;
      }
      if (range.min === min) {
        if (range.max === max || (range.max && range.max - 1 === max)) {
          newList.splice(index as any, 1);
          return newList;
        }
        newList[index] = {
          min: max ? max + 1 : max,
          max: range.max,
        };
        return newList;
      }

      if (
        range.max === max ||
        (range.max && range.max - 1 === max) ||
        (!range.max && !max)
      ) {
        newList[index] = {
          min: range.min,
          max: min ? min - 1 : min,
        };
        return newList;
      }
      if (!min || !max) {
        continue;
      }
      if ((!range.min || range.min < min) && (!range.max || range.max > max)) {
        newList[index] = {
          min: range.min,
          max: min - 1,
        };
        newList.push({
          min: max + 1,
          max: range.max,
        });
        return newList;
      }
    }

    return newList;
  }

  regroupRanges(ranges: Array<Range>) {
    const res = [];
    const notHandledEl = [...ranges];
    let currentElement: Range | null = null;

    while (notHandledEl.length > 0) {
      const index = this.findMinIndexInRanges(notHandledEl);
      if (index === -1) {
        break;
      }
      const range = { ...notHandledEl[index] };
      if (!currentElement || !range.min) {
        currentElement = range;
      } else if (
        currentElement.max === range.min ||
        currentElement.max === range.min - 1
      ) {
        currentElement.max = range.max;
      } else {
        res.push(currentElement);
        currentElement = range;
      }
      notHandledEl.splice(index, 1);
    }
    if (currentElement) {
      res.push(currentElement);
    }
    return res;
  }

  findMinIndexInRanges(ranges: Array<Range>) {
    if (!ranges.length) {
      return -1;
    }
    let minIndex = 0;
    let currMin = ranges[0];
    if (!currMin.min) {
      return 0;
    }
    ranges.forEach((range, index) => {
      if (!currMin.min) {
        return;
      }
      if (!range.min || range.min < currMin.min) {
        currMin = range;
        minIndex = index;
      }
    });
    return minIndex;
  }

  handleParticipantField(field: ParticipantField): boolean {
    return !!field.range && !!field.validation;
  }

  createEmptyCriterionFromParticipantField(field: ParticipantField): Criterion {
    if (!field.range || !field.validation) {
      throw Error('Not handled');
    }

    const { min, max } = field.validation;
    return {
      id: uuidv4(),
      fieldRef: field.name,
      screenerCriterion: false,
      selectionBehavior: SelectionBehavior.REQUIRED,
      listValueType: ListValueType.AGE_RANGE,
      allowList: [{ min: min || null, max: max || null }],
      denyList: [],
    };
  }

  setCriterionValues(
    criterion: Criterion,
    values?: CriterionListItem[],
    choices?: FieldChoice[],
  ): Criterion {
    const all = choices?.map(({ value }) => value).filter(Boolean) as
      | Range[]
      | undefined;

    if (!values) {
      return {
        ...criterion,
        allowList: all,
        denyList: [],
        automaticlyAcceptedList: [],
      };
    }

    const newCriterion = { ...criterion };
    newCriterion.allowList = [];
    newCriterion.denyList = all || [{ min: null, max: null }];
    newCriterion.automaticlyAcceptedList = [];

    for (const value of values) {
      if (!value || typeof value !== 'object' || 'location' in value) {
        continue;
      }

      newCriterion.denyList = this.removeRangeInList(
        newCriterion.denyList as Range[],
        value,
      );
      newCriterion.allowList = this.addRangeInList(
        newCriterion.allowList as [] as Range[],
        value,
      );
    }

    return newCriterion;
  }

  areItemListEquals(item1?: CriterionListItem, item2?: CriterionListItem) {
    if (!item1 && !item2) {
      return true;
    }

    if (
      !item1 ||
      typeof item1 !== 'object' ||
      !item2 ||
      typeof item2 !== 'object'
    ) {
      return false;
    }

    if ('min' in item1) {
      if (!('min' in item2) || item1.min !== item2.min) {
        return false;
      }
    }
    if ('max' in item1) {
      if (!('max' in item2) || item1.max !== item2.max) {
        return false;
      }
    }
    return true;
  }
}
